import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { AutogenLayoutInvoiceProcessingCreditRebillSlideout } from "./autogen/AutogenLayoutInvoiceProcessingCreditRebillSlideout";
import { Button, ChangeEvent, ClickEvent, DropdownItem, ForcedCase, LookupModelSelectionEvent, PanelProps, Snackbar, TableColumn, TableRow, TableRowDisplayEvent, Textbox, TextboxProps, Toast,  DropdownSelectionEvent } from "@mcleod/components";
import { Alignment, Api, CurrencySettings, ModelRow } from "@mcleod/core";
import { getExciseTaxControlBoolean } from "./models/ModelExciseTaxControl";

const ORDER_LOOKUP: Partial<TextboxProps> = {
    lookupModel: "lme/datafusion/orders-lookup",
    lookupModelLayout: "lme/datafusion/OrderIdLookup",
    lookupModelResultField: "id",
    lookupModelDisplayField: "id",
    lookupModelMinChars: 3,
    forcedCase: ForcedCase.UPPER,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: false,
    lookupModelAllowSearchAll: true,
    lookupModelAllowShowAllResults: true
}

const INVOICE_LOOKUP: Partial<TextboxProps> = {
    lookupModel: "lme/ar/invoice-no-lookup",
    lookupModelLayout: "lme/ar/InvoiceNoLookup",
    lookupModelResultField: "invoice_no_string",
    lookupModelDisplayField: "invoice_no_string",
    lookupModelMinChars: 1,
    forcedCase: ForcedCase.UPPER,
    lookupModelAllowFreeform: false,
    lookupModelAllowMultiSelect: false,
    lookupModelAllowSearchAll: true,
    lookupModelAllowShowAllResults: true
}

export class InvoiceProcessingCreditRebillSlideout extends AutogenLayoutInvoiceProcessingCreditRebillSlideout {
    private _buttonRebill: Button;
    private _errorMessage: string[] = [];
    private _successMessage: string = null;
    private _needsDFCI: boolean = false;

    constructor(panelProps?: Partial<PanelProps>) {
        super({
            id: "slideoutPrintPost",
            fillRow: true,
            fillHeight: true,
            padding: 8,
            ...panelProps
        });
        this.initializeButtonNextStep();
    }

    override onLoad() {
        this.textboxRebillBy.addChangeListener((event: ChangeEvent) => {
            this.setLookupByType(this.textboxRebillBy.selectedItem, this.textboxNumber, event.userInitiatedChange);
        });
        this.checkboxCreateCreditMemo.checked = true;
        this.checkboxRetransfer.checked = true;

        this.setExciseTaxColumnVisibility();
    }

    setLookupByType(item: DropdownItem, textbox: Textbox, userInitiatedChange: boolean) {
        const lookupType = item?.value;
        if (userInitiatedChange) {
            textbox.clear();
        }
        if (lookupType === "O") {
            textbox.setProps(ORDER_LOOKUP);
            textbox.caption = "Order Number";
        } else {
            textbox.setProps(INVOICE_LOOKUP);
            textbox.caption = "Invoice Number";
        }

        const tableRow = TableRow.getContainingTableRow(textbox);
        if (tableRow != null) {
            textbox.displayData(tableRow.data, null, 0);
        }
    }

    private initializeButtonNextStep() {
        this._buttonRebill = new Button({
            id: "buttonRebill",
            caption: "Rebill",
            color: "primary.reverse",
            backgroundColor: "primary",
            visible: true,
            width: 140,
            height: 35,
            borderWidth: 1,
            rowBreak: false,
            borderColor: "primary",
            onClick: async (event: ClickEvent) => {
                const missingIndicator = await this.verifyRequiredFields();
                if(missingIndicator)
                    Snackbar.showWarningSnackbar("Correction indicator is required for one or more DataFusion customers.");
                else
                    await this.rebillAction(event);
            },
        });
    }

    public get buttonNextStep(): Button {
        return this._buttonRebill;
    }

    async verifyRequiredFields(): Promise<boolean> {
        let missingIndicator = false;
        const selected = this.tableBillingHistory.selectedRows.map((tableRow) => {
            const corrIndTxtBox = tableRow?.findComponentById("textboxCorrectionInd") as Textbox;
            if(corrIndTxtBox?.required && corrIndTxtBox?.text === "") {
                missingIndicator = true;
            }
        });
        return missingIndicator;
    }

    async rebillAction(event: ClickEvent) {
        this._buttonRebill.busy = true;
        const selected = this.tableBillingHistory.selectedRows.map((tableRow) => {
            const corrIndTxtBox = tableRow?.findComponentById("textboxCorrectionInd") as Textbox;
            return {
                id: tableRow.data.get("id"),
                customer_corr_ind: corrIndTxtBox?.text
            }
        });

        const count = selected.length;

        if (count > 0) {
            const response = await Api.post("lme/ar/rebill",
                {
                    billing_id_list: selected,
                    create_credit_memo: this.checkboxCreateCreditMemo.checked,
                    retransfer_order: this.checkboxRetransfer.checked,
                    new_customer_id: this.switchNewCustomer.checked ? this.textboxNewCustomerId.lookupModelData[0].get("id") : null,
                    reason_code: this.textboxReason.text
                }
            );
            if (response.data[0].error_message) {
                this.errorMessage = response.data[0].error_message;
            }
            if (response.data[0].success_message) {
                this.successMessage = response.data[0].success_message;
            }
            this.slideOut();
        } else {
            Snackbar.showWarningSnackbar("Please select an invoice row (or rows) in the table.");
        }
        this._buttonRebill.busy = false;
    }

    async onLookupModelSelected(event: LookupModelSelectionEvent) {
        let filter;
        if (event.hasSelection()) {
            if (this.textboxRebillBy.selectedItem.value === "O") {
                const id = event.selection.get("id");
                filter = { order_id: id };
            } else {
                const id = event.selection.get("invoice_no_string");
                filter = { invoice_no_string: id };
            }
            await this.sourceBillingHistory.search(filter);
        }
    }

    onNewCustomerSwitchOnChange(event: ChangeEvent) {
        this.textboxNewCustomerId.visible = event.newValue;
    }

    tableBillingHistoryOnRowDisplay(event: TableRowDisplayEvent) {
        const currRow = event.target as TableRow;
        const currModelRow = currRow.data as ModelRow;
        const corrIndTxtBox = currRow?.findComponentById("textboxCorrectionInd") as Textbox;
        if(corrIndTxtBox) {
            const corrIndReq = currModelRow.get("corr_ind_required");
            corrIndTxtBox.required = corrIndReq;
            corrIndTxtBox.enabled = corrIndReq;

            if(currModelRow && corrIndReq) {
                const currCorrInd = currModelRow.get("correction_ind");
                const defaultCorrInd = currModelRow.get("default_correction_ind");
                const version = currModelRow.get("version");
                const corrInd = currCorrInd ? currCorrInd : defaultCorrInd;
                corrIndTxtBox.printable = false;

                corrIndTxtBox.text = corrInd;
                corrIndTxtBox.lookupModel = "lme/datafusion/edi-standard-code";
                corrIndTxtBox.lookupModelLayout = "lme/datafusion/EdiStandardCodeLookup";
                corrIndTxtBox.lookupModelResultField = "standard_code";
                corrIndTxtBox.addBeforeLookupModelSearchListener(event => {
                    event.filter.element_input = "202";
                    event.filter.version_input = version;
                });
            }
        }
    }

    sbhAfterExecution() {
        this.tableBillingHistory.rows.some((tableRow) => {tableRow.data.get("corr_ind_required") ? this._needsDFCI = true : this._needsDFCI = false;});
        const corrIndColumn: TableColumn = this.tableBillingHistory.columns.find(c => c.headingCell.id === "columnHeaderCorrInd");
        if(!this._needsDFCI) {
            if(corrIndColumn) {
                this.tableBillingHistory.removeColumn(corrIndColumn.headingCell.id);
            }
        } else {
            if(!corrIndColumn) {
                const corrIndColumn: TableColumn = new TableColumn({ headingDef: { caption: "DF Correction Indicator", id: "columnHeaderCorrInd" }, cell: { field: "correction_ind" } });
                //corrIndColumn.headingCell.id = "columnHeaderCorrInd";
                corrIndColumn.cell.components = [
                    new Textbox({
                        id: "textboxCorrectionInd",
                        caption: "DF Correction Indicator",
                        lookupModel: "lme/datafusion/edi-standard-code",
                        lookupModelLayout: "lme/datafusion/EdiStandardCodeLookup",
                        lookupModelResultField: "standard_code",
                        lookupModelDisplayField: "standard_code",
                        lookupModelMinChars: 1,
                        forcedCase: ForcedCase.UPPER,
                        lookupModelAllowFreeform: false,
                        lookupModelAllowMultiSelect: false,
                        lookupModelAllowShowAllResults: true,
                        required: true
                    })
                ];
                this.tableBillingHistory.addColumn(corrIndColumn, false, true);
                // corrIndColumn.addBeforeLookupModelSearchListener((event) => {
                //     this.corrIndBeforeLookupModelSearch(event);
                // });
            }
        }
    }

    makeCorrIndTooltipContent(corrIndTxtBox: Textbox, corrInd: string, version: string) {
        if(corrInd != "") {
            CommonTooltips.setTooltipContentLayout(corrIndTxtBox, null, "lme/datafusion/EdiStandardCodeLookup", null,
                { "version": version, "standard_code": corrInd });
            corrIndTxtBox.tooltipDynamicOptions = { position: Alignment.LEFT };
        }
    }

    async corrIndBeforeLookupModelSearch(event) {
        const ediVersion = this.sourceBillingHistory.activeRow?.get("version", "004010");
        event.filter.version_input = ediVersion;
        event.filter.element_input = "202";
    }

    private setExciseTaxColumnVisibility() {
        if (!getExciseTaxControlBoolean("is_active", false)) {
            const taxesColumn = this.tableBillingHistory.columns.find(c => c.headingCell.id === "columnHeaderTaxes");
            const totalWithTaxesColumn = this.tableBillingHistory.columns.find(c => c.headingCell.id === "columnHeaderTotalWithTaxes");

            // Hide columns by removing them
            if (taxesColumn) {
                this.tableBillingHistory.removeColumn(taxesColumn.headingCell.id);
            }
            if (totalWithTaxesColumn) {
                this.tableBillingHistory.removeColumn(totalWithTaxesColumn.headingCell.id);
            }
        }
    }


    get successMessage(): string {
        return this._successMessage;
    }

    set successMessage(value: string) {
        this._successMessage = value;
    }

    get errorMessage(): string[] {
        return this._errorMessage;
    }

    set errorMessage(value: string[]) {
        this._errorMessage = value;
    }

/** This is an event handler for the onLookupModelSelection event of textboxNewCustomerId. */
   textboxNewCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
  // Only perform logic if multicurrency is enabled
    if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
        // Get selected customer currency_type
        const selectedCustomer = this.textboxNewCustomerId.lookupModelData?.[0];
        const customerCurrency = selectedCustomer?.get("currency_type");

        // Get the currency type for total_charges_c from the selected billing row
        // Adjust the field name as needed for your data model
        const selectedBillingRow = this.sourceBillingHistory.activeRow;
        const totalChargesCurrency = selectedBillingRow?.get("total_charges_c");

        if (customerCurrency && totalChargesCurrency && customerCurrency !== totalChargesCurrency) {
            CommonDialogs.showMessage("Selected customer's currency does not match the invoice currency.", "Currency Mismatch",  {panelTitleProps: { backgroundColor: "primary.darker", color: "default.reverse" }}).then(() => {
                this.textboxNewCustomerId.clear();
            });
        }
    }
  }
}
